/* eslint-disable @typescript-eslint/no-explicit-any */
import { AuthenticatedHttpClient } from "@/lib/axios";
import { MailboxForPlan, MailboxType } from "@/types/mailbox.types";
import {
  DowngradePlanDtoType,
  IActiveSubscriptionPlan,
  IAddonTestPricing,
  ICheckoutSessionResponse,
  ICreateCheckoutAddonPayload,
  ICancelSenderProfileSubscriptionPayload,
  ICreateSenderProfileSubscriptionPayload,
  ICustomPlanRequestPayload,
  ICustomerPortalSessionResponse,
  IInvoiceResponse,
  IPaymentMethodResponse,
  IPendingCancellationResponse,
  IPlanCancellationRequestPayload,
  IPlanDetailResponse,
  IPurchasePlanPayload,
  ISenderProfilePlansResponse,
  IUpgradePlanPayload,
  IUpgradePlanResponse,
  IUsageResponseWithNull,
  TestAddonType,
  UpdatePlanDtoType,
} from "@/types/subscription.types";

export class SubscriptionService {
  private http: AuthenticatedHttpClient;

  constructor(httpClient: AuthenticatedHttpClient) {
    this.http = httpClient;
  }

  async getCurrentSubscriptionPlans(): Promise<IActiveSubscriptionPlan[]> {
    const response = await this.http.get<IActiveSubscriptionPlan[]>(
      "/subscription/active-subscriptions"
    );
    return response as unknown as IActiveSubscriptionPlan[];
  }

  async createCheckoutSession(
    data: IPurchasePlanPayload
  ): Promise<ICheckoutSessionResponse> {
    const response = await this.http.post<ICheckoutSessionResponse>(
      "/stripe/create-checkout-session",
      data
    );
    return response as unknown as ICheckoutSessionResponse;
  }

  async createPaymentStepSession(): Promise<ICheckoutSessionResponse> {
    const response = await this.http.post<ICheckoutSessionResponse>(
      "/stripe/payment-step-session"
    );

    return response as unknown as ICheckoutSessionResponse;
  }

  async createAddonTestSession(
    payload: ICreateCheckoutAddonPayload
  ): Promise<ICheckoutSessionResponse> {
    const response = await this.http.post<ICheckoutSessionResponse>(
      "/stripe/create-checkout-addon-session",
      payload
    );

    return response as unknown as ICheckoutSessionResponse;
  }

  async createCustomerPortalSession(): Promise<ICustomerPortalSessionResponse> {
    const response = await this.http.post<ICustomerPortalSessionResponse>(
      "/stripe/portal-session"
    );
    return response as unknown as ICustomerPortalSessionResponse;
  }

  async buyNewPlan(
    data: IPurchasePlanPayload
  ): Promise<ICheckoutSessionResponse> {
    const response = await this.http.post<ICheckoutSessionResponse>(
      "/subscription/buy-new-plan",
      data
    );
    return response as unknown as ICheckoutSessionResponse;
  }

  async getPaymentMethods(): Promise<IPaymentMethodResponse> {
    const response = await this.http.get<IPaymentMethodResponse>(
      "/subscription/payment-methods"
    );

    console.log(response, "payment methods response");
    return response as unknown as IPaymentMethodResponse;
  }

  async updateDefaultPaymentMethod(
    paymentMethodId: string
  ): Promise<IPaymentMethodResponse["defaultPaymentMethodId"]> {
    const response = await this.http.post<string>(
      "/subscription/payment-methods/default",
      {
        paymentMethodId,
      }
    );

    return response as unknown as IPaymentMethodResponse["defaultPaymentMethodId"];
  }

  async deletePaymentMethod(paymentMethodId: string): Promise<{
    message: string;
  }> {
    const response = await this.http.post<{
      message: string;
    }>("/subscription/payment-methods/detach", {
      paymentMethodId,
    });

    return response as unknown as {
      message: string;
    };
  }

  async getInvoices(startingAfter?: string): Promise<IInvoiceResponse> {
    const response = await this.http.get<IInvoiceResponse>(
      "/subscription/invoices",
      {
        params: {
          startingAfter,
        },
      }
    );

    return response as unknown as IInvoiceResponse;
  }

  async buyMoreMailboxes(
    subscriptionId: string,
    planId: string,
    quantity: number
  ): Promise<void> {
    await this.http.post("/subscription/buy-more-mailboxes", {
      subscriptionId,
      planId,
      quantity,
    });
  }

  async getUsage(): Promise<IUsageResponseWithNull> {
    const response = await this.http.get<IUsageResponseWithNull>(
      "/subscription/usage"
    );
    return response as unknown as IUsageResponseWithNull;
  }

  async assignPlan(
    subscriptionId: string,
    mailboxId: string,
    planId: string
  ): Promise<MailboxType> {
    const response = await this.http.post<MailboxType>(
      "/subscription/assign-plan",
      {
        mailboxId,
        subscriptionId,
        planId,
      }
    );
    return response as unknown as MailboxType;
  }

  async unassignPlan(
    subscriptionId: string,
    mailboxId: string,
    planId: string
  ): Promise<MailboxType> {
    const response = await this.http.post<MailboxType>(
      "/subscription/unassign-plan",
      {
        mailboxId,
        subscriptionId,
        planId,
      }
    );
    return response as unknown as MailboxType;
  }

  async getMailboxesForPlan(
    subscriptionId: string,
    planId: string
  ): Promise<MailboxForPlan[]> {
    const response = await this.http.get<MailboxForPlan[]>(
      `/subscription/subscription-mailboxes/${subscriptionId}/${planId}`
    );
    return response as unknown as MailboxForPlan[];
  }

  async upgradePlan(data: UpdatePlanDtoType): Promise<IUpgradePlanResponse> {
    const response = await this.http.post<IUpgradePlanResponse>(
      "/subscription/upgrade-plan",
      data
    );
    return response as unknown as IUpgradePlanResponse;
  }

  async getPlanMetrics(planId: string) {
    const response = await this.http.get(`/downgrade/plan-metrics/${planId}`);
    return response;
  }

  async sendDowngradeFeedback(
    reasons: string[],
    additionalFeedback?: string
  ): Promise<void> {
    await this.http.post("/downgrade/feedback", {
      reasons,
      additionalFeedback,
    });
  }

  async sendPlanCancellationRequest(
    payload: IPlanCancellationRequestPayload
  ): Promise<void> {
    await this.http.post("/subscription/cancel-plan", payload);
  }

  async getPendingCancellations(): Promise<IPendingCancellationResponse[]> {
    const response = await this.http.get<IPendingCancellationResponse[]>(
      "/downgrade/pending-cancellations"
    );
    return response as unknown as IPendingCancellationResponse[];
  }

  async getPendingCancellationForPlan(
    planId: string
  ): Promise<IPendingCancellationResponse | null> {
    const response = await this.http.get<IPendingCancellationResponse>(
      `/downgrade/pending-cancellation/${planId}`
    );
    return response as unknown as IPendingCancellationResponse;
  }

  async revokeCancellation(
    cancellationId: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.http.delete<{
      success: boolean;
      message: string;
    }>(`/downgrade/revoke-cancellation/${cancellationId}`);
    return response as unknown as { success: boolean; message: string };
  }

  async revokeDowngrade(
    downgradeId: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.http.delete<{
      success: boolean;
      message: string;
    }>(`/downgrade/revoke-downgrade/${downgradeId}`);
    return response as unknown as { success: boolean; message: string };
  }

  async getPlanDetailsByType(planType: string) {
    const response = await this.http.get(
      `/subscription/plan-type-details/${planType}`
    );
    return response;
  }

  async getPlanComparisonData(currentPlanId: string, targetPlanType: string) {
    const response = await this.http.get(
      `/subscription/plan-comparison/${currentPlanId}/${targetPlanType}`
    );
    return response as unknown as any;
  }

  async getAddonPlan(planId: string) {
    const response = await this.http.get(`/subscription/addon-plan/${planId}`);
    return response as unknown as any;
  }

  async downgradePlan(data: DowngradePlanDtoType) {
    const response = await this.http.post("/subscription/downgrade-plan", data);
    return response as unknown as any;
  }

  async getAddonTestPricing(type: TestAddonType) {
    const response = await this.http.get(
      `/subscription/get-active-addon-price/${type}`
    );
    return response as unknown as IAddonTestPricing[];
  }

  async createSenderProfileSubscription(
    data: ICreateSenderProfileSubscriptionPayload
  ): Promise<ICheckoutSessionResponse> {
    const response = await this.http.post<ICheckoutSessionResponse>(
      "/stripe/create-sender-profile-subscription",
      data
    );
    return response as unknown as ICheckoutSessionResponse;
  }

  async getSenderProfilePlans(): Promise<ISenderProfilePlansResponse> {
    const response = await this.http.get<ISenderProfilePlansResponse>(
      "/subscription/sender-profile-plans"
    );
    return response as unknown as ISenderProfilePlansResponse;
  }

  async getPlanDetail(
    subscriptionId: string,
    planId: string
  ): Promise<IPlanDetailResponse> {
    const response = await this.http.get<IPlanDetailResponse>(
      `/subscription/plan-detail/${subscriptionId}/${planId}`
    );
    return response as unknown as IPlanDetailResponse;
  }

  async cancelSenderProfileSubscription(
    payload: ICancelSenderProfileSubscriptionPayload
  ): Promise<{ success: boolean; message?: string }> {
    const response = await this.http.post<{ success: boolean; message?: string }>(
      "/stripe/cancel-sender-profile-subscription",
      payload
    );
    return response as unknown as { success: boolean; message?: string };
  }

  async createCustomPlanRequest(
    payload: ICustomPlanRequestPayload
  ): Promise<{ _id: string; message?: string }> {
    const response = await this.http.post<{ _id: string; message?: string }>(
      "/subscription/custom-plan-request",
      payload
    );
    return response as unknown as { _id: string; message?: string };
  }

  async getAllowedEspTypes(): Promise<{
    organizationId: string;
    allowedEspTypes: string[];
    allEspAllowed: boolean;
  }> {
    const response = await this.http.get<{
      organizationId: string;
      allowedEspTypes: string[];
      allEspAllowed: boolean;
    }>("/subscription-analytics/my-allowed-esp-types");
    return response as unknown as {
      organizationId: string;
      allowedEspTypes: string[];
      allEspAllowed: boolean;
    };
  }
}
